import { ACTION_SCROLL_EXPERIENCE_LIST } from 'utils/analytics/eventTypeConstantsAmplitude';
import { AnalyticsParams } from 'hooks/search/useSearchHandlers';
import { AvailabilitySearchQuery, LockOptions } from 'types';
import { ExperienceCardAction } from 'utils/searchBar';
import { ExperienceResultSimple } from 'components/www/availability-search/ExperienceResultSimple';
import { FunctionComponent, useMemo } from 'react';
import { LocalDateTime } from 'js-joda';
import { StoreType } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { Typography } from 'omakase';
import { consumer, core } from '@tock/coretypes';
import { ga4SendEvent } from 'components/consent-restricted-scripts/GoogleAnalytics';
import { getLockedDateTimeFor, getLockedSeatingAreaFor } from 'utils/lock';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useCurrentMetroArea } from 'hooks/useCurrentMetroArea';
import { useOnce } from '@tock/hooks';
import { useSearchHandlerContext } from 'components/search/SearchContext';
import BusinessHeader from 'business/BusinessHeader';
import pluralize from 'pluralize';

const overall = ({ spacing }: Theme) => css`
  padding-bottom: ${spacing(3)}px;
`;

const list = ({ breakpoints, layout, spacing }: Theme) => css`
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding-left: ${spacing(3)}px;
  margin-bottom: ${spacing(1)}px;
  ${layout.hideScrollbarAsString};
  ${breakpoints.up('md')} {
    display: block;
    padding-right: ${spacing(3)}px;
    margin-bottom: 0;
  }
`;

const listSingle = css`
  margin-bottom: 0;
`;

const listItem = ({ breakpoints, spacing }: Theme) => css`
  display: flex;
  position: relative;
  margin-right: ${spacing(1)}px;
  flex: 1;
  min-width: calc(100vw - ${spacing(7)}px);
  &:last-child {
    padding-right: ${spacing(3)}px;
    // The spacing(4) here comes from reducing the min-width specified above by
    // 3 fewer spacing units, as compared to the spacing(7) above.
    min-width: calc(100vw - ${spacing(4)}px);
  }

  ${breakpoints.up('md')} {
    margin-right: 0;
    margin-bottom: ${spacing(1)}px;
    min-width: 100%;
    &:last-child {
      padding-right: 0;
      margin-bottom: 0;
      // This needs to be repeated because otherwise the specificity of the &:last-child min-width
      // style specified earlier in listItem will win over the min-width: 100% specified just above.
      min-width: 100%;
    }
  }
`;

const listItemSingle = ({ breakpoints, spacing }: Theme) => css`
  flex: 1;
  margin-right: 0;
  max-width: 100%;
  &:last-child {
    padding-right: ${spacing(3)}px;
    ${breakpoints.up('md')} {
      padding-right: 0;
    }
  }
`;

const header = ({ spacing }: Theme) => css`
  padding-bottom: ${spacing(2)}px !important;
`;

const footer = ({ breakpoints, spacing }: Theme) => css`
  padding-left: ${spacing(3)}px;
  padding-right: ${spacing(3)}px;
  ${breakpoints.up('md')} {
    display: none;
  }
`;

interface Props {
  business: consumer.OfferingBusiness;
  distanceMeters?: number;
  lock: StoreType['lock'];
  locale: string;
  onLockTimeAndSize(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    business: consumer.OfferingBusiness,
    analyticsParams?: AnalyticsParams
  ): void;
  onNavigateToExperience(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    experienceListIdx: number,
    businessListIdx: number,
    date?: string
  ): void;
  onNavigateToExperienceMenu(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering,
    idx?: number
  ): void;
  timeZone: string;
  query: AvailabilitySearchQuery;
  hideExperiences: boolean;
  searchOfferings: consumer.SearchOffering[];
  experienceCardAction: ExperienceCardAction;
  index: number;
}

/**
 * Create a new type that associates a SearchTicketGroup with an array of seating area ids instead of one id
 */
export interface SearchTicketGroupWithSeatingAreas
  extends Omit<consumer.SearchTicketGroup, 'seatingArea'> {
  seatingAreas: core.SeatingArea[];
}

interface SearchOfferingGroupedSeatingAreas extends consumer.SearchOffering {
  seatingAreas?: core.SeatingArea[];
}

interface OfferingWithLockedDateTime {
  offering: SearchOfferingGroupedSeatingAreas;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
}

// exported for testing
export const sortAndFilterOfferings = (
  searchOfferings: consumer.SearchOffering[],
  size?: number,
  currentLock?: core.TicketGroup,
  type?: consumer.OfferingSearchType
): OfferingWithLockedDateTime[] => {
  const processedOfferings = searchOfferings
    .filter((offering) => offering.ticketGroup.length > 0)
    .sort((offering1, offering2) => {
      // Sort events by chronological order to match how we display events on business page
      if (type === 'EVENT') {
        const offeringAvailableDateTime1 = Math.min(
          ...offering1.ticketGroup.map((ticketGroup) =>
            Date.parse(ticketGroup.availableDateTime ?? '')
          )
        );
        const offeringAvailableDateTime2 = Math.min(
          ...offering2.ticketGroup.map((ticketGroup) =>
            Date.parse(ticketGroup.availableDateTime ?? '')
          )
        );

        return offeringAvailableDateTime1 - offeringAvailableDateTime2;
      } else {
        return offering1.ticketGroup.length - offering2.ticketGroup.length;
      }
    })
    .map((offering) => {
      const offeringId = offering?.offering?.id;

      const lockedDateTime =
        currentLock && offeringId && size
          ? getLockedDateTimeFor(currentLock, offeringId, size)
          : undefined;

      const lockedSeatingArea =
        currentLock && offeringId && size
          ? getLockedSeatingAreaFor(currentLock, offeringId, size)
          : undefined;

      return {
        offering,
        lockedDateTime: lockedDateTime ?? undefined,
        lockedSeatingArea: lockedSeatingArea ?? undefined,
      };
    });

  return groupTicketGroupsByAreas([...processedOfferings]);
};

// Consolidate all SearchTicketGroup items by time and add an array
// of available seatingAreas to the SearchTicketGroup
//
// exported for testing
export const groupTicketGroupsByAreas = (
  offerings: OfferingWithLockedDateTime[]
): OfferingWithLockedDateTime[] => {
  return offerings.map((offering) => {
    const groupedByDateTime = offering.offering.ticketGroup.reduce<
      Record<string, SearchTicketGroupWithSeatingAreas>
    >((acc, currSearchTicketGroup) => {
      const { availableDateTime, seatingArea, price, exclusivity } = currSearchTicketGroup;

      if (availableDateTime) {
        if (!acc[availableDateTime]) {
          acc[availableDateTime] = {
            availableDateTime,
            price: price ?? 0,
            exclusivity: exclusivity ?? 'NONE',
            seatingAreas: [],
          };
        }

        if (seatingArea?.id && seatingArea?.name) {
          const { id, name } = seatingArea;
          acc[availableDateTime].seatingAreas.push({
            id,
            name,
          });
        }
      }

      return acc;
    }, {});

    return {
      offering: {
        ...offering.offering,
        ticketGroup: Object.values(groupedByDateTime),
      },
      lockedDateTime: offering.lockedDateTime,
      lockedSeatingArea: offering.lockedSeatingArea,
    };
  });
};

const AvailabilitySearchResultSimple: FunctionComponent<Props> = ({
  business,
  experienceCardAction,
  distanceMeters,
  hideExperiences,
  locale,
  lock,
  onLockTimeAndSize,
  onNavigateToExperience,
  onNavigateToExperienceMenu,
  query,
  searchOfferings,
  timeZone,
  index,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  // splitting results into ticketGroup and offering at this level helps remove ambiguity about undefined types in the ExperienceResult component
  const processedOfferings = useMemo(
    () => sortAndFilterOfferings(searchOfferings, query.size, lock.currentLock, query.type),
    [searchOfferings, lock.currentLock, query.size, query.type]
  );
  const { analyticsSearchContext } = { ...useSearchHandlerContext() };

  const metroArea = useCurrentMetroArea();

  const handleScroll = useOnce(() => {
    window.analytics.sendEvent(
      'Citywide search',
      'Citywide search Experience list scrolled',
      `${business.name} - ${processedOfferings.length}`
    );
    const data = {
      searchContext: analyticsSearchContext,
      businessName: business.name,
      businessListIndex: index,
    };
    amplitudeSendEvent(ACTION_SCROLL_EXPERIENCE_LIST, data);
    ga4SendEvent('scroll_experience_list', data);
  });

  const handleSelectDateAndTime = (
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    ticketTypeName?: string
  ): void => {
    onLockTimeAndSize(ticketTypeId, selectedSize, dateTime, priceCents, options, business, {
      ticketTypeName,
      businessListIndex: index,
    });
  };

  const isSingleOffering = processedOfferings.length === 1;

  return (
    <div css={overall}>
      <BusinessHeader
        goBackTo
        css={header}
        business={business}
        distanceMeters={distanceMeters}
        locale={metroArea?.locale}
        index={index}
      />

      {!hideExperiences && (
        <ul onScroll={handleScroll} css={[list, isSingleOffering && listSingle]}>
          {processedOfferings.map((processedOffering, idx) => {
            const offering = processedOffering.offering.offering as consumer.Offering;
            if (!processedOffering.offering?.offering?.id) {
              return null;
            }
            const { lockedDateTime, lockedSeatingArea } = processedOffering;

            // console.log('debug - processedoffering', processedOffering);
            return (
              <li
                css={[listItem, isSingleOffering && listItemSingle]}
                key={offering.id}
                data-testid="search-experience-item"
                data-experience={offering.id}
              >
                <ExperienceResultSimple
                  business={business}
                  locale={locale}
                  query={query}
                  lockedDateTime={lockedDateTime ?? undefined}
                  lockedSeatingArea={lockedSeatingArea ?? undefined}
                  onLockTimeAndSize={handleSelectDateAndTime}
                  onNavigateToExperience={(business, offering, date) =>
                    onNavigateToExperience(business, offering, idx, index, date)
                  }
                  onNavigateToExperienceMenu={() =>
                    onNavigateToExperienceMenu(business, offering, idx)
                  }
                  offering={offering}
                  ticketGroup={
                    processedOffering.offering.ticketGroup as SearchTicketGroupWithSeatingAreas[]
                  }
                  timeZone={timeZone}
                  experienceCardAction={experienceCardAction}
                />
              </li>
            );
          })}
        </ul>
      )}
      {!isSingleOffering && (
        <Typography variant="caption" css={footer}>
          {pluralize('Experience', processedOfferings.length, true)}
        </Typography>
      )}
    </div>
  );
};
export default AvailabilitySearchResultSimple;
